import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { api } from "api";
import {
  GuardRails,
  PaymentTable,
  StrategyConfigV2,
  StrategyGenerationClientContext,
  StrategyResponse,
} from "dtos/strategy.dto";
import { T_Api_Success_Res } from "./api.types";
import { LedgerDto } from "dtos/ledger.dto";
import { useDebounce } from "hooks/use-debounce";

export type BusinessFinancials = {
  business_name: string;
  short_put: boolean;
  total_debt: number;
  active_lawsuits: number;
  active_loans: number;
  monthly_revenue: number;
  profit_margin: number;
  hardship_score: string;
  hardship_text: string;
};

export const useGetStrategies = (
  payload: Partial<{
    appKey: string;
    total_weeks: number;
    weekly_budget: number;
    format: StrategyConfigV2["format"];
    type: StrategyConfigV2["resolution_type"];
    orgCode: string;
  }>,
) => {
  return useQuery({
    queryKey: ["get-strategies", payload],
    queryFn: async () => {
      const { data } = await api.get<
        T_Api_Success_Res<{
          strategies: StrategyResponse[];
          selected: string | undefined;
          meta: BusinessFinancials;
          type: StrategyConfigV2["format"];
          non_eligible: {
            slug: string;
            error: unknown;
            guardRails: Partial<GuardRails>;
          }[];
        }>
      >("/application/strategy", {
        params: { ...payload, dont_fail: true },
      });

      return data;
    },
    enabled: Boolean(payload.appKey?.length && payload.orgCode?.length),
  });
};

export type SelectStrategyParams = Partial<{
  appKey: string;
  total_weeks: number;
  weekly_budget: number;
  slug: string;
}>;

export const useSelectStrategy = () => {
  return useMutation({
    mutationFn: async ({ slug, ...payload }: SelectStrategyParams) => {
      const { data } = await api.post<
        T_Api_Success_Res<{
          selectedConfig: StrategyConfigV2;
          strategy: StrategyResponse;
          paymentTable: PaymentTable;
        }>
      >(`/application/strategy/${slug}`, {}, { params: payload });

      return data;
    },
  });
};

export const usePreviewStrategy = ({ slug, ...payload }: SelectStrategyParams) => {
  return useQuery({
    queryKey: ["strategy-preview", slug, payload],
    queryFn: async () => {
      const { data } = await api.get<
        T_Api_Success_Res<{
          selectedConfig: StrategyConfigV2;
          strategy: StrategyResponse;
          paymentTable: PaymentTable;
          ledgerEntries: LedgerDto[];
        }>
      >(`/application/strategy/${slug}`, { params: payload });

      return data;
    },
    enabled: Boolean(slug?.length),
  });
};

export const useGetAllStrategyConfigs = (payload: { orgCode: string }) => {
  return useQuery({
    queryKey: ["get-strategy-configs", payload],
    queryFn: async () => {
      const { data } = await api.get<T_Api_Success_Res<StrategyConfigV2[]>>(
        `/organisation/${payload.orgCode}/strategy`,
      );
      return data.result;
    },
    enabled: Boolean(payload.orgCode?.length),
  });
};

export const useCreateStrategyConfig = ({ orgCode }: { orgCode: string }) => {
  return useMutation({
    mutationFn: async (payload: Omit<StrategyConfigV2, "slug">) => {
      const { data } = await api.post(`/organisation/${orgCode}/strategy`, payload);
      return data;
    },
  });
};

export const useEditStrategyConfig = ({ orgCode }: { orgCode: string }) => {
  return useMutation({
    mutationFn: async ({ slug, ...payload }: StrategyConfigV2) => {
      const { data } = await api.patch(`/organisation/${orgCode}/strategy`, payload, { params: { slug } });
      return data;
    },
  });
};

export const useDeleteStrategyConfig = ({ orgCode }: { orgCode: string }) => {
  return useMutation({
    mutationFn: async ({ slug }: Pick<StrategyConfigV2, "slug">) => {
      const { data } = await api.delete(`/organisation/${orgCode}/strategy?slug=${slug}`);
      return data;
    },
  });
};

export const useGetCustomConfigurationPreview = ({
  config: rawConfig,
  context,
  retainOldState,
}: {
  config: StrategyConfigV2 | undefined;
  context: StrategyGenerationClientContext | undefined;
  retainOldState?: boolean;
}) => {
  const config = useDebounce(rawConfig);
  return useQuery({
    queryKey: ["custom-strategy-preview", context, config],
    async queryFn() {
      const { data } = await api.post<
        T_Api_Success_Res<{
          strategy: StrategyResponse;
          payment_table: PaymentTable;
          ledger: LedgerDto[];
        }>
      >("/application/strategy/custom-preview", { config, context });
      return data.result;
    },
    enabled: Boolean(config?.format) && Boolean(context?.appKey),
    placeholderData: retainOldState ? keepPreviousData : undefined,
  });
};
